import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { UserStore } from '../../store/user/user.store';
import { ProviderService } from '../../services/provider/provider.service';
import { firstValueFrom, map } from 'rxjs';

function normalizeDate(value: any): string | null {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toISOString().slice(0, 10);
}

function isExpired(value: any): boolean {
  const normalized = normalizeDate(value);

  if (!normalized) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const expiration = new Date(`${normalized}T00:00:00`);
  expiration.setHours(0, 0, 0, 0);

  // The expiration date itself counts as expired.
  return expiration <= today;
}

function getLicenceExpireDate(provider: any): string | null {
  return normalizeDate(
    provider?.license_expire_date ??
    provider?.licence_expire_date ??
    provider?.license_expiration_date ??
    provider?.licence_expiration_date ??
    provider?.license_expiry_date ??
    provider?.licence_expiry_date ??
    null
  );
}

function getInsuranceExpireDate(provider: any): string | null {
  return normalizeDate(
    provider?.insurance_expire_date ??
    provider?.malpractice_expire_date ??
    provider?.insurance_expiration_date ??
    provider?.malpractice_expiration_date ??
    provider?.insurance_expiry_date ??
    provider?.malpractice_expiry_date ??
    null
  );
}

function hasExpiredRequiredDocuments(provider: any): boolean {
  return isExpired(getLicenceExpireDate(provider)) || isExpired(getInsuranceExpireDate(provider));
}

function extractProviderList(response: any): any[] {
  if (Array.isArray(response)) {
    return response;
  }

  const providers = response?.providers ?? response?.result ?? response?.data ?? response?.items ?? [];
  return Array.isArray(providers) ? providers : [];
}

function isProvidersRenewalPage(url: string): boolean {
  return url.startsWith('/profile/providers');
}

async function getClinicProviderForExpiryCheck(
  providerService: ProviderService,
  provider: any
): Promise<any> {
  const clinicId = Number(provider?.clinic_id ?? 0);
  const providerId = Number(provider?.provider_id ?? provider?.id ?? 0);

  if (!clinicId || !providerId) {
    return provider;
  }

  try {
    const response = await firstValueFrom(
      providerService.getAllProidersByClinic(clinicId).pipe(
        map((value: any) => value)
      )
    );

    const clinicProviders = extractProviderList(response);
    const matchedProvider = clinicProviders.find((clinicProvider: any) =>
      Number(clinicProvider?.id ?? 0) === providerId ||
      Number(clinicProvider?.provider_id ?? 0) === providerId ||
      (!!provider?.email_address && clinicProvider?.email_address === provider.email_address) ||
      (!!provider?.npi && clinicProvider?.npi === provider.npi)
    );

    return matchedProvider ? { ...provider, ...matchedProvider } : provider;
  } catch (error) {
    console.error('Failed to load clinic providers for expiry-date authorization check:', error);
    return provider;
  }
}

function setUserFromProvider(userStore: any, provider: any, expiryProvider: any): boolean {
  const licenceExpireDate = getLicenceExpireDate(expiryProvider);
  const insuranceExpireDate = getInsuranceExpireDate(expiryProvider);
  const hasExpiredDocuments = hasExpiredRequiredDocuments(expiryProvider);

  userStore.setParams({
    id: provider?.id ?? expiryProvider?.id ?? 0,
    active: provider?.active ?? expiryProvider?.active ?? false,
    clinic_id: provider?.clinic_id ?? expiryProvider?.clinic_id ?? 0,
    provider_id: provider?.provider_id ?? expiryProvider?.id ?? 0,
    onboarding_status: provider?.onboarding_status ?? expiryProvider?.onboarding_status ?? 0,
    licence_expire_date: licenceExpireDate,
    license_expire_date: licenceExpireDate,
    insurance_expire_date: insuranceExpireDate,
    has_expired_documents: hasExpiredDocuments
  });

  return hasExpiredDocuments;
}

export const providerAccessGuard: CanActivateFn & CanActivateChildFn = async (route, state) => {
  const router = inject(Router);
  const userStore = inject(UserStore);
  const providerService = inject(ProviderService);

  const currentUrl = state.url ?? '';

  if (Number(userStore.id()) > 0) {
    if (!userStore.active()) {
      return router.createUrlTree(['/onboarding']);
    }

    if (userStore.has_expired_documents() && !isProvidersRenewalPage(currentUrl)) {
      return router.createUrlTree(['/profile/providers']);
    }

    return true;
  }

  try {
    const provider = await firstValueFrom(
      providerService.getActiveProvider().pipe(
        map((response: any) => response)
      )
    );

    const providerForExpiryCheck = await getClinicProviderForExpiryCheck(providerService, provider);
    const hasExpiredDocuments = setUserFromProvider(userStore, provider, providerForExpiryCheck);

    if (!provider?.active) {
      return router.createUrlTree(['/onboarding']);
    }

    if (hasExpiredDocuments && !isProvidersRenewalPage(currentUrl)) {
      return router.createUrlTree(['/profile/providers']);
    }

    return true;
  } catch (error) {
    console.error('Provider access guard error:', error);
    return router.createUrlTree(['/login']);
  }
};
